import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type { ApiError } from "@/lib/api"
import { apiRequest } from "@/lib/api"
import type { Project, PushSubscriber } from "@/types"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import type {
  ColumnDef,
  PaginationState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table"
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  ArrowDown,
  ArrowUp,
  Bell,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Loader2,
  MonitorSmartphone,
  Search,
  SlidersHorizontal,
} from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"

type SubscribersResponse = {
  subscribers: PushSubscriber[]
  next_cursor: number | null
  prev_cursor: number | null
  total_count: number
}

type SubscribersPageProps = {
  currentProject: Project | null
}

const formatDate = (iso: string | null) => {
  if (!iso) return "\u2014"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "\u2014"
  return d.toLocaleDateString("pt-BR", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

const parseUserAgent = (ua: string | null) => {
  if (!ua) return "Dispositivo desconhecido"

  let browser = "Navegador desconhecido"
  if (ua.includes("Firefox")) browser = "Firefox"
  else if (ua.includes("Edg")) browser = "Edge"
  else if (ua.includes("Chrome")) browser = "Chrome"
  else if (ua.includes("Safari") && !ua.includes("Chrome")) browser = "Safari"

  let os = "Sistema desconhecido"
  if (ua.includes("Windows")) os = "Windows"
  else if (ua.includes("Mac OS")) os = "macOS"
  else if (ua.includes("Linux") && !ua.includes("Android")) os = "Linux"
  else if (ua.includes("Android")) os = "Android"
  else if (ua.includes("iOS") || ua.includes("iPhone") || ua.includes("iPad")) os = "iOS"

  if (browser === "Navegador desconhecido" && os === "Sistema desconhecido") {
    return "Dispositivo desconhecido"
  }

  return `${browser} em ${os}`
}

const formatDateTime = (iso: string | null) => {
  if (!iso) return "\u2014"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "\u2014"
  return d.toLocaleString("pt-BR", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

const SubscribersPage = ({ currentProject }: SubscribersPageProps) => {
  const { projectSlug } = useParams<{ projectSlug: string }>()

  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [cursor, setCursor] = useState<number | string | null>(null)
  const [direction, setDirection] = useState<"next" | "prev">("next")
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const [sorting, setSorting] = useState<SortingState>([
    { id: "created_at", desc: true },
  ])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    fcm_token: false,
    endpoint: false,
    user_agent: false,
    country: false,
    subscribed_url: false,
    metadata: false,
  })

  const sortBy = sorting[0]?.id ?? "created_at"
  const sortOrder = sorting[0]?.desc ? "desc" : "asc"

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
      setCursor(null)
      setDirection("next")
      setPagination((prev) => ({ ...prev, pageIndex: 0 }))
    }, 300)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCursor(null)
    setDirection("next")
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [sortBy, sortOrder])

  const limit = pagination.pageSize
  const serverPage = pagination.pageIndex + 1

  const queryParams = useMemo(() => {
    const params = new URLSearchParams()
    if (debouncedSearch) params.set("q", debouncedSearch)
    if (cursor) params.set("cursor", String(cursor))
    params.set("limit", String(limit))
    params.set("direction", direction)
    params.set("sort_by", sortBy)
    params.set("sort_order", sortOrder)
    return params.toString()
  }, [debouncedSearch, cursor, direction, limit, sortBy, sortOrder])

  const { data, isLoading, isError, error, isFetching } = useQuery<
    SubscribersResponse,
    ApiError
  >({
    queryKey: ["subscribers", projectSlug, debouncedSearch, cursor, direction, limit, sortBy, sortOrder],
    queryFn: () =>
      apiRequest<SubscribersResponse>(
        `/api/projects/${encodeURIComponent(projectSlug || "")}/push_subscribers?${queryParams}`
      ),
    enabled: Boolean(projectSlug),
    placeholderData: keepPreviousData,
  })

  const subscribers = data?.subscribers ?? []
  const nextCursor = data?.next_cursor ?? null
  const prevCursor = data?.prev_cursor ?? null
  const totalCount =
    typeof data?.total_count === "number" ? data.total_count : subscribers.length
  const totalPages = Math.max(1, Math.ceil(totalCount / limit))

  const columns = useMemo<ColumnDef<PushSubscriber>[]>(
    () => [
      {
        id: "subscriber",
        header: "Inscrito",
        cell: ({ row }) => {
          const { external_user_id, fcm_token, user_agent } = row.original
          const title = external_user_id?.trim() || "Anônimo"
          const subtitle = parseUserAgent(user_agent)
          
          return (
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary border border-primary/20">
                {title.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex flex-col">
                <span className="truncate font-medium text-sm text-foreground" title={external_user_id || "Anônimo"}>
                  {title}
                </span>
                <span className="truncate text-xs text-muted-foreground flex items-center gap-1 mt-0.5" title={user_agent ?? fcm_token}>
                  <MonitorSmartphone className="h-3 w-3" />
                  {subtitle}
                </span>
              </div>
            </div>
          )
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue() as PushSubscriber["status"]
          return (
            <Badge
              variant={status === "active" ? "secondary" : "outline-solid"}
              className={
                status === "active"
                  ? "bg-green-500/10 text-green-600 hover:bg-green-500/20 border-green-500/20 text-[11px]"
                  : "text-[11px] text-muted-foreground"
              }
            >
              {status === "active" ? "Ativo" : "Inativo"}
            </Badge>
          )
        },
      },
      {
        accessorKey: "fcm_token",
        header: "FCM Token",
        cell: ({ getValue }) => {
          const value = getValue() as string
          return (
            <span className="block max-w-[200px] truncate font-mono text-xs text-muted-foreground" title={value}>
              {value}
            </span>
          )
        },
        enableSorting: false,
      },
      {
        accessorKey: "last_seen_at",
        header: "Visto por último",
        cell: ({ getValue }) => {
          const value = getValue() as string | null
          return (
            <span className="text-sm text-muted-foreground">
              {formatDateTime(value)}
            </span>
          )
        },
      },
      {
        accessorKey: "unsubscribed_at",
        header: "Desinscrito",
        cell: ({ getValue }) => {
          const value = getValue() as string | null
          return (
            <span className="text-sm text-muted-foreground">
              {formatDateTime(value)}
            </span>
          )
        },
      },
      {
        accessorKey: "endpoint",
        header: "Endpoint",
        cell: ({ getValue }) => {
          const value = getValue() as string | null
          return value ? (
            <span className="block max-w-[340px] truncate text-sm" title={value}>
              {value}
            </span>
          ) : (
            <span className="text-muted-foreground">\u2014</span>
          )
        },
        enableSorting: false,
      },
      {
        accessorKey: "user_agent",
        header: "Agente do usuário",
        cell: ({ getValue }) => {
          const value = getValue() as string | null
          return value ? (
            <span className="block max-w-[340px] truncate text-sm" title={value}>
              {value}
            </span>
          ) : (
            <span className="text-muted-foreground">\u2014</span>
          )
        },
        enableSorting: false,
      },
      {
        accessorKey: "country",
        header: "País",
        cell: ({ getValue }) => {
          const value = getValue() as string | null
          return value ? (
            <span className="text-sm">{value}</span>
          ) : (
            <span className="text-muted-foreground">\u2014</span>
          )
        },
      },
      {
        accessorKey: "subscribed_url",
        header: "URL de inscrição",
        cell: ({ getValue }) => {
          const value = getValue() as string | null
          return value ? (
            <span className="block max-w-[340px] truncate text-sm" title={value}>
              {value}
            </span>
          ) : (
            <span className="text-muted-foreground">\u2014</span>
          )
        },
        enableSorting: false,
      },
      {
        accessorKey: "metadata",
        header: "Metadados",
        cell: ({ getValue }) => {
          const metadata = getValue() as Record<string, unknown>
          const keys = Object.keys(metadata ?? {})
          if (keys.length === 0) return <span className="text-muted-foreground">\u2014</span>
          return (
            <div className="flex max-h-6 flex-wrap gap-1 overflow-hidden">
              {keys.slice(0, 2).map((key) => (
                <Badge key={key} variant="secondary" className="max-w-[160px] truncate text-[11px]">
                  {key}: {String(metadata[key])}
                </Badge>
              ))}
              {keys.length > 2 && (
                <Badge variant="outline" className="text-[11px]">
                  +{keys.length - 2}
                </Badge>
              )}
            </div>
          )
        },
        enableSorting: false,
      },
      {
        accessorKey: "created_at",
        header: "Criado",
        cell: ({ getValue }) => {
          const value = getValue() as string | null
          return (
            <span className="text-sm text-muted-foreground">{formatDate(value)}</span>
          )
        },
      },
    ],
    []
  )

  const table = useReactTable({
    data: subscribers,
    columns,
    state: { sorting, columnVisibility, pagination },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id),
    manualPagination: true,
    manualSorting: true,
    rowCount: totalCount,
  })

  const isTableLoading = isLoading || isFetching
  const rowModel = table.getRowModel()

  const columnLabels: Record<string, string> = {
    subscriber: "Inscrito",
    status: "Status",
    fcm_token: "FCM Token",
    last_seen_at: "Visto por último",
    unsubscribed_at: "Desinscrito",
    endpoint: "Endpoint",
    user_agent: "Agente do usuário",
    country: "País",
    subscribed_url: "URL de inscrição",
    metadata: "Metadados",
    created_at: "Criado",
  }

  const handleNext = () => {
    if (!nextCursor) return
    setDirection("next")
    setCursor(nextCursor)
    setPagination((prev) => ({
      ...prev,
      pageIndex: Math.min(prev.pageIndex + 1, Math.max(totalPages - 1, 0)),
    }))
  }

  const handlePrev = () => {
    if (!prevCursor) return
    setDirection("prev")
    setCursor(prevCursor)
    setPagination((prev) => ({
      ...prev,
      pageIndex: Math.max(prev.pageIndex - 1, 0),
    }))
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-2xl font-semibold tracking-tight">
            <span>Inscritos Push</span>
            {!isError && data != null ? (
              <span className="text-base font-normal text-muted-foreground">
                <span className="font-semibold tabular-nums text-foreground">
                  {totalCount.toLocaleString("pt-BR")}
                </span>{" "}
                {totalCount === 1 ? "inscrito" : "inscritos"}
                {debouncedSearch ? " encontrados" : " no total"}
              </span>
            ) : isLoading ? (
              <span className="text-base font-normal text-muted-foreground">Carregando…</span>
            ) : null}
          </h1>
          <p className="text-sm text-muted-foreground">
            Gerencie os inscritos de notificações push para {currentProject?.name ?? "este projeto"}.
          </p>
        </div>
      </div>

      <div className="rounded-lg border bg-card text-card-foreground">
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar inscritos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8">
                  <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />
                  Colunas
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Alternar colunas</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) => column.toggleVisibility(!!value)}
                    >
                      {columnLabels[column.id] ?? column.id}
                    </DropdownMenuCheckboxItem>
                  ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex items-center gap-2">
              <Label className="whitespace-nowrap text-xs text-muted-foreground">Linhas</Label>
              <Select
                value={String(limit)}
                disabled={isTableLoading}
                onValueChange={(value) => {
                  const nextLimit = Number(value)
                  setPagination((prev) => ({
                    ...prev,
                    pageSize: nextLimit,
                    pageIndex: 0,
                  }))
                  setCursor(null)
                  setDirection("next")
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 30, 50].map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="w-full max-w-full overflow-x-auto">
          <Table className="table-fixed min-w-full w-max">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={header.column.getCanSort() ? "cursor-pointer select-none" : ""}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder ? null : (
                        <div className="flex items-center gap-1">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() &&
                            ({
                              asc: <ArrowUp className="ml-1 h-3.5 w-3.5" />,
                              desc: <ArrowDown className="ml-1 h-3.5 w-3.5" />,
                            }[header.column.getIsSorted() as string] ?? (
                              <ChevronsUpDown className="ml-1 h-3 w-3 text-muted-foreground/50" />
                            ))}
                        </div>
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isTableLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-32 text-center">
                    <div className="flex items-center justify-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Carregando inscritos...
                    </div>
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-32 text-center">
                    <p className="text-sm text-destructive">
                      {error?.message ?? "Falha ao carregar inscritos."}
                    </p>
                  </TableCell>
                </TableRow>
              ) : rowModel.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-32 text-center">
                    <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
                      <Bell className="h-8 w-8" />
                      <p className="text-sm font-medium">Nenhum inscrito encontrado</p>
                      <p className="text-xs">
                        {search ? "Tente ajustar sua busca." : "Os inscritos aparecerão após as inscrições de push."}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                rowModel.rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="overflow-hidden">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between border-t px-4 py-3">
          <p className="text-sm text-muted-foreground">
            {subscribers.length > 0
              ? `Mostrando ${rowModel.rows.length} de ${totalCount} inscritos`
              : "Nenhum inscrito"}
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={handlePrev}
              disabled={!prevCursor || isTableLoading}
              title="Página anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="mx-2 tabular-nums text-sm text-muted-foreground">
              {serverPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={handleNext}
              disabled={!nextCursor || isTableLoading}
              title="Página seguinte"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubscribersPage
